import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { APIResponse } from '../common/common.model';

import {
    Attachment,
    AttachmentModelSetTransformer,
    Playlist,
    PlaylistModelSetTransformer,
    TrackVideoEvent,
    TrackVideoEventTransformer,
    ViewedPlaylistItem,
    ViewedPlaylistModelSetTransformer,
} from './vimeo.model';

@Injectable()
export class VimeoService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listPlaylists(): Observable<Playlist[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.VIMEO}/vimeo/playlist`)
            .pipe(
                // TODO consolidate
                map((data: APIResponse) => {
                    if (!data.isSuccess) {
                        throw new Error(data.message);
                    }
                    return data.entity;
                }),
                map((model) =>
                    new PlaylistModelSetTransformer().transform(model),
                ),
                catchError(() => of([])),
            );
    }

    listAttachments(playlistItemId: number): Observable<Attachment[]> {
        const params = new HttpParams().set(
            'playlistItemId',
            playlistItemId.toString(),
        );

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.VIMEO}/vimeo/attachment`, {
                params,
            })
            .pipe(
                // TODO consolidate
                map((data: APIResponse) => {
                    if (!data.isSuccess) {
                        throw new Error(data.message);
                    }
                    return data.entity;
                }),
                map((model) =>
                    new AttachmentModelSetTransformer().transform(model),
                ),
                catchError(() => of([])),
            );
    }

    listViewedPlaylistItems(): Observable<ViewedPlaylistItem[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.VIMEO}/vimeo/viewedPlaylistItems`,
            )
            .pipe(
                // TODO consolidate
                map((data: APIResponse) => {
                    if (!data.isSuccess) {
                        throw new Error(data.message);
                    }
                    return data.entity;
                }),
                map((model) =>
                    new ViewedPlaylistModelSetTransformer().transform(model),
                ),
                catchError(() => of([])),
            );
    }

    createTrackVideoEvent(
        trackVideoEvent: TrackVideoEvent,
    ): Observable<boolean> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.VIMEO}/vimeo/trackVideoEvent`,
                new TrackVideoEventTransformer().reverseTransform(
                    trackVideoEvent,
                ),
                { observe: 'response' },
            )
            .pipe(
                map((data) => data.status === 201),
                catchError(() => of(false)),
            );
    }
}
